import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Flex, notification, Spin, Pagination } from "antd";
import { useTranslation } from "react-i18next";

import {
  AvatarGroup,
  StyledSearchBar,
  StyledSortIcon,
  StyledFilterButton,
} from "../../../../../../components/common";

import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { fetchTrainingProgramsStart } from "../../../../../../store/features/training-programs/training-programs-slice";
import {
  deleteTrainingProgramStart,
  updateTrainingProgramStart,
} from "../../../../../../store/features/training-program/training-program-slice";

import { routes } from "../../../../../../navigation/routes";
import { IMAGE_BASE_URL, PAGE_SIZE } from "../../../../../../common/config";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { StyledDropdown } from "../../../header/style";
import { ReactComponent as PlusIcon } from "../../../../../../assets/icons/WhitePlus.svg";
import DeleteModal from "../../../../../../components/common/DeleteModal";
import { SortType } from "../../../../../../interfaces/screens/fetch-params-types";
import { capitalizeFirstLetter } from "../../../../../../utils/utils";
import {
  StatusTag,
  StyledFiltersBar,
} from "../../../../../../components/common/styles";
import ActiveUsersModal from "./ActiveUsersModal";
import {
  StyledExtendedTableCell,
  StyledExtendedCellImage,
  StyledExtendedCellContent,
  StyledExtendedCellTitle,
  StyledTableActionsButton,
  StyledTable,
} from "../../../../../../components/common/styles/tableStyles";
import { StyledThreeDotsVertical } from "./style";
import { IFetchParams } from "../../../../../../interfaces/base";

export const TrainingProgramsTable = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const { id: trainerId } = useParams<{ id: string }>();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openActiveUsersModal, setOpenActiveUsersModal] =
    useState<boolean>(false);

  const [currentTrainingProgramId, setCurrentTrainingProgramId] =
    useState<string>("");
  const [currentTotalAccounts, setCurrentTotalAccounts] = useState<
    number | null
  >(null);

  const { trainingPrograms, loading, error, metadata, tableState } =
    useAppSelector((state) => state.trainingPrograms);
  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: trainingProgramError,
    operation: trainingProgramOperation,
    requestState: trainingProgramRequestState,
    loading: trainingProgramLoading,
  } = useAppSelector((state) => state.trainingProgram);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: PAGE_SIZE, skip: 0 }
        : pagination;

    fetchTrainingPrograms({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || trainingProgramError) {
      openNotification(
        trainingProgramError?.status === 1000
          ? trainingProgramError?.message
          : undefined
      );
    }
  }, [error, trainingProgramError]);

  useEffect(() => {
    if (!openDeleteModal && !openActiveUsersModal) {
      setCurrentTrainingProgramId("");
      setCurrentTotalAccounts(null);
    }
  }, [openDeleteModal, openActiveUsersModal]);

  useEffect(() => {
    if (
      trainingProgramRequestState === "finish" &&
      (trainingProgramOperation === "delete" ||
        trainingProgramOperation === "update")
    ) {
      setOpenDeleteModal(false);
      fetchTrainingPrograms({});
    }
  }, [trainingProgramRequestState, trainingProgramOperation]);

  const openNotification = (error?: string) => {
    api["error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const columns = [
    {
      title: t("Program Name"),
      dataIndex: "name",
      key: "name",
      render: (_: string, record: any) => {
        return (
          <StyledExtendedTableCell>
            <StyledExtendedCellImage
              src={IMAGE_BASE_URL + "/" + record.trainingProgram.outerCover}
            />
            <StyledExtendedCellContent>
              <StyledExtendedCellTitle>
                {record.trainingProgram.name}
              </StyledExtendedCellTitle>
            </StyledExtendedCellContent>
          </StyledExtendedTableCell>
        );
      },
    },
    {
      title: t("Group"),
      dataIndex: "group",
      key: "group",
      render: (_: string, record: any) => (
        <span>{t(capitalizeFirstLetter(record.trainingProgram.group))}</span>
      ),
    },
    {
      title: t("Difficulty"),
      dataIndex: "difficulty",
      key: "difficulty",
      render: (_: string, record: any) => (
        <span>{t(record.trainingProgram.difficulty)}</span>
      ),
    },
    {
      title: t("Number of Workouts per Week"),
      dataIndex: "timesPerWeek",
      key: "timesPerWeek",
      render: (_: string, record: any) => (
        <span>{record.trainingProgram.timesPerWeek}</span>
      ),
    },
    {
      title: t("Active Users"),
      dataIndex: "activeUsers",
      key: "activeUsers",
      render: (_: any, record: any) => {
        return (
          <>
            {record.accounts && record.accounts.length > 0 ? (
              <Flex
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setCurrentTrainingProgramId(record.trainingProgram._id);
                  setCurrentTotalAccounts(record.totalAccounts);
                  setOpenActiveUsersModal(true);
                }}
              >
                <AvatarGroup
                  numAcountsHidden={
                    record.totalAccounts - record.accounts.slice(0, 5).length
                  }
                  entities={record.accounts.slice(0, 5)}
                />
              </Flex>
            ) : (
              <span>-</span>
            )}
          </>
        );
      },
    },
    {
      title: t("Program Status"),
      dataIndex: "enabled",
      key: "enabled",
      render: (_: any, record: any) => (
        <StatusTag active={record.trainingProgram.enabled}>
          {t(record.trainingProgram.enabled ? "Active" : "Inactive")}
        </StatusTag>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_: any, record: any) => {
        const actionsMenuItems = [
          {
            key: "edit",
            label: t("Edit"),
            onClick: () =>
              navigate(
                `${routes.appRoutes.trainers}/${trainerId}/training-programs/${record.trainingProgram._id}`,
                {
                  state: { isEdit: true },
                }
              ),
          },
          {
            key: "updateStatus",
            label:
              t("Change Status To-") +
              " " +
              (record.trainingProgram.enabled ? t("Inactive") : t("Active")),
            onClick: () => {
              dispatch(
                updateTrainingProgramStart({
                  trainingProgramId: record.trainingProgram._id,
                  trainingProgram: { enabled: !record.trainingProgram.enabled },
                })
              );
            },
          },
          {
            key: "delete",
            label: t("Delete"),
            onClick: () => {
              setCurrentTrainingProgramId(record.trainingProgram._id);
              setOpenDeleteModal(true);
            },
          },
        ];

        return (
          <StyledTableActionsButton
            menu={{
              items: actionsMenuItems.map((item) => ({
                key: item.key,
                label: item.label,
                onClick: item.onClick,
              })),
            }}
            trigger={["click"]}
          >
            <StyledThreeDotsVertical />
          </StyledTableActionsButton>
        );
      },
    },
  ];

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: PAGE_SIZE * (page - 1),
    };

    fetchTrainingPrograms({ pagination: newPagination });
  };

  const handleDelete = () => {
    dispatch(
      deleteTrainingProgramStart({
        trainingProgramId: currentTrainingProgramId,
      })
    );
  };

  const handleSetSort = (value: SortType) => {
    fetchTrainingPrograms({
      sort: value,
      pagination: { page: 0, limit: PAGE_SIZE, skip: 0 },
    });
  };

  const fetchTrainingPrograms = (body: IFetchParams) => {
    dispatch(
      fetchTrainingProgramsStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
        filter: { trainer: trainerId },
      })
    );
  };

  return (
    <>
      {contextHolder}
      <TrainingProgramsTableHeader
        trainerId={trainerId}
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
      />
      <Spin spinning={loading || trainingProgramLoading}>
        <StyledTable
          columns={columns}
          dataSource={trainingPrograms}
          pagination={false}
          rowKey={(record: any) => record.trainingProgram._id}
          locale={{ emptyText: t("No results") }}
        />
        <Pagination
          current={metadata?.page}
          pageSize={PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={trainingProgramLoading}
        />
        <ActiveUsersModal
          open={openActiveUsersModal}
          setOpen={setOpenActiveUsersModal}
          trainingProgramId={currentTrainingProgramId}
          totalAccounts={currentTotalAccounts}
        />
      </Spin>
    </>
  );
};

export const TrainingProgramsTableHeader = (props: {
  trainerId: string | undefined;
  search: string;
  setSearch: (search: string) => void;
  setSort: (sort: SortType) => void;
}) => {
  const { trainerId, search, setSearch, setSort } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { tableState } = useAppSelector((state) => state.trainingPrograms);
  const currentSortBy = tableState.sort.sortBy;

  const handleCreateNewTrainingProgram = () => {
    navigate(`/trainers/${trainerId}/training-programs/add`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSort = (e: any) => {
    setSort({ sortBy: e.key, sortOrder: e.key === "updatedAt" ? -1 : 1 });
  };

  const sortByOptions = [
    {
      key: "name",
      label: t("Sort by A-Z"),
    },
    {
      key: "updatedAt",
      label: t("Sort by Last Update"),
    },
  ];

  return (
    <>
      <StyledFiltersBar>
        <Button
          type="primary"
          shape="round"
          onClick={handleCreateNewTrainingProgram}
          icon={<PlusIcon width={8} height={8} />}
          iconPosition="end"
        >
          {t("Create New Training Program")}
        </Button>
        <StyledSearchBar
          placeholder={t("Search") + "..."}
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
        <StyledDropdown
          menu={{
            items: sortByOptions,
            onClick: handleSort,
            selectedKeys: [currentSortBy],
          }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ width: 150, direction: "rtl" }}
        >
          <StyledFilterButton type="text" icon={<StyledSortIcon />}>
            {t("Sort")}
          </StyledFilterButton>
        </StyledDropdown>
      </StyledFiltersBar>
    </>
  );
};
